import fs from 'node:fs'
import path from 'node:path'
import type { LinterInfo } from '@cli-types/next-adapter/config.js'
import type { OxfmtScaffoldResult } from '@cli-types/next-adapter/init.js'
import {
  IGNORE_PATTERNS,
  OXFMT_CONFIG_FILE,
  OXLINT_CONFIG_FILE
} from '@cli-constants/lint-configs.js'
import { normalizePosixDir } from '@cli-utils/paths.js'
import { addLintScripts } from './add-lint-scripts.js'

/** oxfmt sorts Tailwind classes against a stylesheet entrypoint, here `<adminDir>/admin-globals.css`. */
export function scaffoldOxfmt(
  cwd: string,
  linter: LinterInfo,
  adminDir: string
): OxfmtScaffoldResult {
  if (linter.type !== 'none') {
    return {
      installed: false,
      skippedReason: `${linter.type} already configured (${linter.configFile})`
    }
  }

  const formatConfigPath = path.join(cwd, OXFMT_CONFIG_FILE)
  const lintConfigPath = path.join(cwd, OXLINT_CONFIG_FILE)
  if (fs.existsSync(formatConfigPath)) {
    return { installed: false, skippedReason: `${OXFMT_CONFIG_FILE} already exists` }
  }

  const admin = normalizePosixDir(adminDir)
  const namespace = admin.split('/').pop() ?? admin

  const formatConfig = {
    $schema: './node_modules/oxfmt/configuration_schema.json',
    useTabs: false,
    tabWidth: 2,
    // oxfmt's maximum, so import statements stay on one line instead of exploding into a
    // specifier block.
    printWidth: 100,
    singleQuote: true,
    semi: false,
    trailingComma: 'none',
    // Mirrors the CLI's own `.oxfmtrc.json`, so generated code is format-clean the moment it lands.
    sortImports: {
      newlinesBetween: false,
      customGroups: [
        { groupName: 'wildcard', modifiers: ['wildcard'] },
        {
          groupName: 'admin',
          modifiers: ['value'],
          elementNamePattern: [`@${namespace}`, `@${namespace}/**`]
        }
      ],
      groups: [
        'type',
        'wildcard',
        'builtin',
        'admin',
        'external',
        ['internal', 'subpath'],
        'style',
        'unknown',
        ['parent', 'sibling', 'index']
      ]
    },
    sortPackageJson: false,
    sortTailwindcss: {
      stylesheet: `./${admin}/admin-globals.css`,
      functions: ['cn', 'clsx', 'cva', 'tv']
    },
    ignorePatterns: [
      ...IGNORE_PATTERNS,
      '**/*.css',
      '**/*.scss',
      '**/*.md',
      '**/*.mdx',
      '**/*.yaml',
      '**/*.yml',
      '**/*.config.js',
      '**/*.config.mjs',
      '**/*.config.cjs'
    ]
  }

  const lintConfig = {
    $schema: './node_modules/oxlint/configuration_schema.json',
    plugins: ['typescript', 'unicorn', 'oxc', 'react', 'jsx-a11y', 'nextjs', 'import'],
    categories: {
      correctness: 'error'
    },
    env: {
      builtin: true,
      browser: true,
      node: true,
      es2024: true
    },
    rules: {
      'react/react-in-jsx-scope': 'off',
      'react/no-multi-comp': 'error',
      'typescript/no-non-null-assertion': 'off',
      // Inline `type` specifiers become their own `import type` statement, which
      // oxfmt then hoists via the `type` group above.
      'import/consistent-type-specifier-style': ['error', 'prefer-top-level']
    },
    // The generated admin vendors shadcn and Tiptap primitives whose a11y and hook shapes
    // BetterStart owns, so the rest of the project stays fully linted.
    overrides: [
      {
        files: [`**/${admin}/**`, `**/(${namespace})/**`],
        rules: {
          'jsx-a11y/prefer-tag-over-role': 'off',
          'jsx-a11y/no-autofocus': 'off',
          'jsx-a11y/anchor-has-content': 'off',
          'react-hooks/exhaustive-deps': 'off',
          'unicorn/no-new-array': 'off',
          'unicorn/no-empty-file': 'off'
        }
      },
      {
        files: [`**/${admin}/components/ui/**`],
        rules: {
          'react/no-multi-comp': 'off'
        }
      }
    ],
    ignorePatterns: IGNORE_PATTERNS
  }

  fs.writeFileSync(formatConfigPath, `${JSON.stringify(formatConfig, null, 2)}\n`, 'utf-8')
  if (!fs.existsSync(lintConfigPath)) {
    fs.writeFileSync(lintConfigPath, `${JSON.stringify(lintConfig, null, 2)}\n`, 'utf-8')
  }
  addLintScripts(cwd)

  return { installed: true, skippedReason: null }
}
